import { gql, useMutation } from '@apollo/client';
import type { Op } from 'quill/core';
import React from 'react';
import { MenuItem } from '../../components/Menu';
import OverflowMenu from '../../components/OverflowMenu';
import { CREATION_ID } from '../../editor/editorReducer';
import type {
  CommentThreadSheet_PostFragment,
  CommentThreadSheet_ThreadFragment,
} from '../../graphql/generated/graphqlTypes';
import RESOLVE_THREAD_MUTATION from '../../graphql/resolveThread';
import checkIcon from '../../icons/check.svg';
import MagneticWallSheet from '../MagneticWallSheet';
import Comment from './Comment';
import CommentCreate from './CommentCreate';
import styles from './CommentThreadSheet.module.scss';
import FoldedComments from './FoldedComments';
import useMagneticWallSheetContext from '../useMagneticWallSheetContext';

export const CommentThreadSheetContext = ({
  id,
  type,
  highlight,
}: Parameters<typeof useMagneticWallSheetContext>[0]) => {
  const { highlighted, before, after } = useMagneticWallSheetContext({
    type,
    id,
    highlight,
  });

  return highlighted && highlighted.length > 0 ? (
    <div className={styles.sheetContextContainer}>
      {before}
      {highlighted}
      {after}
    </div>
  ) : null;
};

const POST_FRAGMENT = gql`
  fragment CommentThreadSheet_post on Post {
    ...Comment_post
  }
  ${Comment.fragments.post}
`;

const THREAD_FRAGMENT = gql`
  fragment CommentThreadSheet_thread on Thread {
    id
    comments {
      id
      ...Comment_comment
    }
  }
  ${Comment.fragments.comment}
`;

const FOCUSED_COMMENT_EXPAND_LIMIT = 6;

interface CommentThreadSheetProps {
  post: CommentThreadSheet_PostFragment;
  thread: CommentThreadSheet_ThreadFragment;
  onClose: () => void;
  onInputChange: (threadId: string, content: Op[]) => void;
  unsavedInputValue?: Op[];
}

const CommentThreadSheet = ({
  post,
  onClose,
  thread,
  onInputChange,
  unsavedInputValue,
}: CommentThreadSheetProps) => {
  const [resolveThread] = useMutation(RESOLVE_THREAD_MUTATION);

  const handleResolve = () => {
    resolveThread({ variables: { id: thread.id } });
  };

  const isCreating = thread.id === CREATION_ID;

  return (
    <MagneticWallSheet
      onClose={onClose}
      title={isCreating ? 'New comment' : 'Comment'}
      headerContent={
        <CommentThreadSheetContext id={thread.id} type="comment" highlight />
      }
      inputRow={
        <CommentCreate
          postId={post.id}
          threadId={thread.id}
          onChange={(value) => onInputChange(thread.id, value)}
          initialContent={unsavedInputValue}
        />
      }
      overflowMenu={
        !isCreating ? (
          <OverflowMenu>
            <MenuItem icon={checkIcon} onClick={handleResolve}>
              Resolve
            </MenuItem>
          </OverflowMenu>
        ) : null
      }
    >
      {!isCreating && (
        <>
          {thread.comments && thread.comments.length > 0 && (
            <FoldedComments
              comments={thread.comments}
              limit={FOCUSED_COMMENT_EXPAND_LIMIT}
              isExpanded
              renderComment={(comment, idx) => (
                <Comment
                  key={comment.id}
                  isExpanded
                  isFirst={idx === 0}
                  comment={comment}
                  threadId={thread.id}
                  onResize={() => {}}
                  post={post}
                />
              )}
            />
          )}
        </>
      )}
    </MagneticWallSheet>
  );
};

CommentThreadSheet.fragments = { post: POST_FRAGMENT, thread: THREAD_FRAGMENT };

export default CommentThreadSheet;
